import { useCallback, useState } from "react";

/**
 * ===============================================================================================
 * A small and focused hook to standardize the table boilerplate:
 * - Advanced filters (metaPair)
 * - Saved views (views Map)
 * - Selected view name
 *
 * Design goals:
 * 1) Keep tables smart (domain rules stay in the table) and helpers dumb.
 * 2) Provide stable, reusable handlers so each table does not rewrite the same logic.
 * 3) Avoid over-abstraction: this hook does NOT compute filtered rows; it only manages state.
 *
 * Typical usage (inside a table component):
 *
 * const {
 *   metaPair, views, selectedView,
 *   addMetaPair, deleteFilter, deleteAllFilters, submitFilters,
 *   resetToDefault, applyView, saveView,
 * } = useTableFilters({ onSubmit: () => setFirst(0) });
 *
 * Then wire it to <AdvanceFilter /> and <ViewsMenu />.
 */

/**
 * @typedef {Object} UseTableFiltersOptions
 * @property {Function=} onSubmit
 * Optional callback called when the user clicks "Submit" in the AdvancedFilter UI.
 * Common use: reset pagination (ex: setFirst(0)).
 *
 * @property {Map<string,string>=} initialViews
 * Optional initial views Map. Defaults to Map([["Default",""]]).
 *
 * @property {string=} defaultViewName
 * Optional name for default view. Defaults to "Default".
 *
 * @property {Array=} initialMetaPair
 * Optional initial filter list. Defaults to [].
 */

/**
 * @param {UseTableFiltersOptions=} options
 */
export function useTableFilters(options = {}) {
    const {
        onSubmit,
        initialViews,
        defaultViewName = "Default",
        initialMetaPair = [],
    } = options;

    // ---------------------------------------------------------------------------------------------
    // Filters state:
    // metaPair is the list of advanced filters selected by the user.
    // Each item is usually shaped like:
    // { name: "origin", operation: "Contains", value: "Lisbon" }
    // ---------------------------------------------------------------------------------------------
    const [metaPair, setMetaPair] = useState(initialMetaPair);

    // ---------------------------------------------------------------------------------------------
    // Views state:
    // views is a Map(viewName -> serialized metaPair JSON).
    // The table decides whether to persist this Map (local state, localStorage, backend, etc.).
    // This hook only manages the in-memory Map.
    // ---------------------------------------------------------------------------------------------
    const [views, setViews] = useState(
        initialViews instanceof Map
            ? initialViews
            : new Map([[defaultViewName, ""]])
    );

    // ---------------------------------------------------------------------------------------------
    // Currently selected view name (purely informational; does not affect filtering by itself).
    // ---------------------------------------------------------------------------------------------
    const [selectedView, setSelectedView] = useState(defaultViewName);

    // =============================================================================================
    // Filter manipulation helpers
    // =============================================================================================

    /**
     * Add or replace a filter by name.
     * If a filter with the same `name` exists, we replace it (so there is only one per field).
     * This matches your current table patterns.
     */
    const addMetaPair = useCallback((filter) => {
        if (!filter?.name) return;

        setMetaPair((prev) => {
            const exists = prev.some((x) => x?.name === filter.name);
            return exists
                ? prev.map((x) => (x?.name === filter.name ? filter : x))
                : [...prev, filter];
        });
    }, []);

    /**
     * Remove a single filter by its meta name (e.g., "origin").
     */
    const deleteFilter = useCallback((name) => {
        if (!name) return;
        setMetaPair((prev) => prev.filter((f) => f?.name !== name));
    }, []);

    /**
     * Clear all active filters.
     */
    const deleteAllFilters = useCallback(() => {
        setMetaPair([]);
    }, []);

    /**
     * Called when the user submits filters in the UI.
     * This hook does not know about pagination; the table can provide onSubmit to reset page.
     */
    const submitFilters = useCallback(() => {
        if (typeof onSubmit === "function") onSubmit();
    }, [onSubmit]);

    // =============================================================================================
    // View helpers
    // =============================================================================================

    /**
     * Reset to default view:
     * - select default view name
     * - clear filters
     * This mirrors the behavior you already use in multiple tables.
     */
    const resetToDefault = useCallback(() => {
        setSelectedView(defaultViewName);
        setMetaPair([]);
    }, [defaultViewName]);

    /**
     * Apply a saved view:
     * - reads the view JSON from the views Map
     * - parses it into metaPair
     * - selects that view
     *
     * Safe behavior:
     * - if the view does not exist or JSON parse fails, it does nothing (no throw).
     */
    const applyView = useCallback(
        (viewName) => {
            if (!viewName) return;

            const str = views.get(viewName);
            if (!str) {
                // Allow selecting a view even if it has an empty string (Default).
                setSelectedView(viewName);
                if (viewName === defaultViewName) setMetaPair([]);
                return;
            }

            try {
                const parsed = JSON.parse(str);
                if (Array.isArray(parsed)) {
                    setMetaPair(parsed);
                    setSelectedView(viewName);
                }
            } catch {
                // Intentionally silent:
                // views may be corrupted or edited manually; we avoid crashing the UI.
            }
        },
        [views, defaultViewName]
    );

    /**
     * Save current filters into a view:
     * - serializes metaPair to JSON
     * - stores it in views Map under viewName
     * - selects that view
     *
     * Note:
     * - This uses a functional setViews update to avoid mutating the existing Map instance.
     * - We still keep Map semantics for your existing ViewsMenu.
     */
    const saveView = useCallback(
        (viewName) => {
            if (!viewName) return;

            const payload = JSON.stringify(metaPair);

            setViews((prev) => {
                const next = new Map(prev);
                next.set(viewName, payload);
                return next;
            });

            setSelectedView(viewName);
        },
        [metaPair]
    );

    /**
     * Optional helper if a table wants to rename a view key.
     * Not used by your current UI, but it is a common need.
     */
    const renameView = useCallback((fromName, toName) => {
        if (!fromName || !toName || fromName === toName) return;

        setViews((prev) => {
            const next = new Map(prev);
            if (!next.has(fromName)) return next;

            const val = next.get(fromName);
            next.delete(fromName);
            next.set(toName, val);

            return next;
        });

        setSelectedView((prevSelected) =>
            prevSelected === fromName ? toName : prevSelected
        );
    }, []);

    /**
     * Optional helper if a table wants to delete a view from the Map.
     * We prevent deleting the default view.
     */
    const deleteView = useCallback(
        (viewName) => {
            if (!viewName || viewName === defaultViewName) return;

            setViews((prev) => {
                const next = new Map(prev);
                next.delete(viewName);
                return next;
            });

            // If the user deleted the currently selected view, fall back to default.
            setSelectedView((prevSelected) => {
                if (prevSelected !== viewName) return prevSelected;
                return defaultViewName;
            });
        },
        [defaultViewName]
    );

    // =============================================================================================
    // render
    // =============================================================================================
    return {
        // Raw state
        metaPair,
        views,
        selectedView,

        // Setters (exposed for edge cases)
        setMetaPair,
        setViews,
        setSelectedView,

        // Filter operations
        addMetaPair,
        deleteFilter,
        deleteAllFilters,
        submitFilters,

        // View operations
        resetToDefault,
        applyView,
        saveView,
        renameView,
        deleteView,

        // Metadata
        defaultViewName,
    };
}
